import {
  FunctionComponent,
  MutableRefObject,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { isObject } from '@/utils/isObject';
import { pointerDownEvent } from '@/utils/pointerEvents';

type FocusContainerRef = MutableRefObject<HTMLDivElement | null>;
type FocusRef = MutableRefObject<{
  containerRef?: FocusContainerRef;
  onClose?: () => void;
  withFocusTrap?: boolean;
  before?: Element | null;
}>;
type Activate = (focusRef: FocusRef) => void;
type Deactivate = (focusRef: FocusRef) => void;
type UseFocus = (
  isActive?: boolean,
  onClose?: () => void,
  withFocusTrap?: boolean
) => [FocusContainerRef, () => void];

const missingProvider = () => {
  throw new Error('Missing FocusProvider');
};

const FocusContext = createContext({
  useFocus: missingProvider as UseFocus,
});

const isHTMLElement = (element: unknown): element is HTMLElement =>
  isObject(element) && 'focus' in element;

type FocusProviderProps = {
  children: ReactNode;
};

export const FocusProvider: FunctionComponent<FocusProviderProps> = ({ children }) => {
  const activeFocusRef: FocusRef = useRef({});

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const { containerRef, withFocusTrap } = activeFocusRef.current;
      const container = containerRef?.current;
      if (container && withFocusTrap && (event.key === 'Tab' || event.keyCode === 9)) {
        const { shiftKey, target } = event;

        const tabbableElements = Array.from(
          container.querySelectorAll('a[href], button, input, textarea')
        ).filter((element) => !(element as { disabled?: boolean }).disabled) as HTMLElement[];

        if (shiftKey) {
          const firstTabbableElement = tabbableElements[0];
          if (target === firstTabbableElement || target === container) {
            event.preventDefault();

            const lastTabbableElement = tabbableElements[tabbableElements.length - 1];
            if (lastTabbableElement) {
              lastTabbableElement.focus();
            }
          }
        } else {
          const lastTabbableElement = tabbableElements[tabbableElements.length - 1];
          if (target === lastTabbableElement) {
            event.preventDefault();
            if (container) {
              container.focus();
            }
          }
        }
      }
    };

    const onPointerDown = (event: PointerEvent | MouseEvent) => {
      const { containerRef, onClose } = activeFocusRef.current;
      const container = containerRef?.current;
      const target = event.target as Element;
      // Do nothing if clicking ref's element or descendent elements
      if (
        container &&
        onClose &&
        !container.contains(target) &&
        !target.matches('#modal-container *')
      ) {
        onClose();
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      const onClose = activeFocusRef.current.onClose;
      if (onClose && event.keyCode === 27) {
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener(pointerDownEvent, onPointerDown, true);
    document.addEventListener('keyup', onKeyUp);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener(pointerDownEvent, onPointerDown, true);
      document.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  const stack: FocusRef[] = [];
  const activate: Activate = (focusRef) => {
    focusRef.current.before = document.activeElement;
    activeFocusRef.current = focusRef.current;
    stack.push(focusRef);
  };
  const deactivate: Deactivate = (focusRef) => {
    const stackIndex = stack.indexOf(focusRef);
    if (stackIndex !== -1) {
      stack.splice(stackIndex, 1);
    }

    activeFocusRef.current = stack.length > 0 ? stack[stack.length - 1].current : {};

    // focus the former active element or fallback to the new active container
    for (const focusElement of [
      focusRef.current.before,
      activeFocusRef.current.containerRef?.current,
    ]) {
      const body = document.body;
      if (isHTMLElement(focusElement) && focusElement !== body && body.contains(focusElement)) {
        focusElement.focus();
        break;
      }
    }
  };

  const useFocus: UseFocus = (isActive = true, onClose, withFocusTrap) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const ref: FocusRef = useRef({ containerRef });
    ref.current.onClose = onClose;
    ref.current.withFocusTrap = withFocusTrap;

    useEffect(() => {
      if (isActive) {
        activate(ref);
        return () => deactivate(ref);
      }
    }, [isActive]);

    const focusOnRef = useCallback(() => isActive && containerRef.current?.focus(), [isActive]);

    return [containerRef, focusOnRef];
  };

  return <FocusContext.Provider value={{ useFocus }}>{children}</FocusContext.Provider>;
};

export const useFocus: UseFocus = (isActive, onClose, withFocusTrap) =>
  useContext(FocusContext).useFocus(isActive, onClose, withFocusTrap);
